import styled from 'styled-components'

import { device } from '@/common/constants/device'

import Text from '@/components/common/Text'
import WrapWithText from 'components/ui/WrapWithText'
import { useTranslations } from 'next-intl'

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  padding: 2rem 0 26.875rem;
  background-image: url('/_next_static/landing/possibilities/bg-heart.webp'),
    url('/_next_static/landing/possibilities/bg-heart-blur.webp');
  background-size: 85px 64px, 101px 80px;
  background-position: left 133px top 216px, left 280px top 341px;
  background-repeat: no-repeat;

  @media ${device.tablet} {
    padding-top: 5rem;
    padding-bottom: 36.25rem;

    background-size: 155px 118px, 128px 101px;
    background-position: left 665px top 211px, left -61px top 441px;
  }

  @media ${device.laptop} {
    background-position: left 832px top 211px, left 80px top 441px;
  }

  @media ${device.laptopL} {
    width: 1400px;
    padding-bottom: 25rem;

    background-size: 119px 90px, 168px 132px;
    background-position: left 1180px top 109px, left -61px top 380px;
  }
`

const Title = styled.h2`
  max-width: 650px;
  text-align: center;
  margin-top: 0.75rem;
  font-weight: 800;
  font-size: 1.625em;
  line-height: 1.23;
  letter-spacing: -0.02em;

  @media ${device.tablet} {
    font-size: 2.5em;
  }
`

const Circle = styled.div`
  display: flex;
  width: 100px;
  height: 100px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  top: 0;
  left: 0;
  z-index: 10;
  padding: 0.25rem;
  position: absolute;
  border: 1px solid #e0e1e2;
  background-color: var(--white);
  box-shadow: 0px 633px 253px rgba(163, 163, 163, 0.01),
    0px 356px 214px rgba(163, 163, 163, 0.05),
    0px 158px 158px rgba(163, 163, 163, 0.09),
    0px 40px 87px rgba(163, 163, 163, 0.1), 0px 0px 0px rgba(163, 163, 163, 0.1);
  border-radius: 100%;
`

const Img = styled.div`
  background-image: url(${({ src }) => src});
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  width: 32px;
  height: 32px;

  @media ${device.tablet} {
    width: ${({ sizeT }) => (sizeT ? `${sizeT}px` : `56px`)};
    height: ${({ sizeT }) => (sizeT ? `${sizeT}px` : `56px`)};
  }
`

const CustomText = styled(Text)`
  text-align: center;
`

const Circle1 = styled(Circle)`
  width: 118px;
  height: 117px;

  left: 15px;
  top: 264px;

  @media ${device.tablet} {
    width: 190px;
    height: 190px;
    left: 70px;
    top: 517px;
  }

  @media ${device.laptop} {
    left: 203px;
  }

  @media ${device.laptopL} {
    left: 394px;
    top: 278px;
  }
`

const Circle2 = styled(Circle)`
  width: 132px;
  height: 130px;

  left: 145px;
  top: 286px;

  @media ${device.tablet} {
    width: 190px;
    height: 190px;
    left: 520px;
    top: 517px;
  }

  @media ${device.laptop} {
    left: 653px;
  }

  @media ${device.laptopL} {
    left: 844px;
    top: 278px;
  }
`

const Circle3 = styled(Circle)`
  width: 106px;
  height: 106px;

  left: 253px;
  top: 206px;

  @media ${device.tablet} {
    width: 190px;
    height: 190px;
    left: 520px;
    top: 295px;
  }

  @media ${device.laptop} {
    left: 653px;
  }

  @media ${device.laptopL} {
    left: 1050px;
    top: 227px;
  }
`

const Circle4 = styled(Circle)`
  width: 166px;
  height: 165px;
  left: 15px;
  top: 385px;

  @media ${device.tablet} {
    width: 228px;
    height: 228px;
    left: 276px;
    top: 360px;
  }

  @media ${device.laptop} {
    left: 409px;
  }

  @media ${device.laptopL} {
    left: 600px;
    top: 312px;
  }
`

const Circle5 = styled(Circle)`
  width: 142px;
  height: 142px;
  left: 189px;
  top: 416px;

  @media ${device.tablet} {
    width: 190px;
    height: 190px;
    left: 70px;
    top: 295px;
  }

  @media ${device.laptop} {
    left: 203px;
  }

  @media ${device.laptopL} {
    left: 188px;
    top: 227px;
  }
`

const Possibilities = () => {
  const t = useTranslations('index')

  return (
    <Wrap id="possibilities">
      <WrapWithText
        textKey="possibilities"
        bgColor="purple"
        color="purple"
        fileName="index"
      />
      <Title>{t('meetTonsOfMarketing')}</Title>
      <Circle1>
        <Img src="/_next_static/landing/possibilities/top.svg" />
        <CustomText size="14" sizeT="20" weight="500" color="black">
          {t('topApps')}
        </CustomText>
      </Circle1>
      <Circle2>
        <Img src="/_next_static/landing/possibilities/fire.svg" />
        <CustomText size="14" sizeT="20" weight="500" color="black">
          {t('applicationAnalysis')}
        </CustomText>
      </Circle2>
      <Circle3>
        <Img src="/_next_static/landing/possibilities/money.svg" />
        <CustomText size="14" sizeT="20" weight="500" color="black">
          {t('variousRates')}
        </CustomText>
      </Circle3>
      <Circle4>
        <Img src="/_next_static/landing/possibilities/lupa.svg" sizeT="64" />
        <CustomText size="14" sizeT="20" weight="500" color="black">
          {t('searchAndselectionOfCreatives')}
        </CustomText>
      </Circle4>
      <Circle5>
        <Img src="/_next_static/landing/possibilities/laptop.svg" />
        <CustomText size="14" sizeT="20" weight="500" color="black">
          {t('workingWithCreatives')}
        </CustomText>
      </Circle5>
    </Wrap>
  )
}

export default Possibilities
